import CtaSection from '@/components/CtaSection'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import { breadcrumbSchema, howToSteps, verificationChecklist, whatsappLink, whatsappMessages } from '@/lib/site-data'
import {CheckCircle2, MessageCircleDashed as MessageCircle} from 'lucide-react'

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como comprar uma página do Instagram com a Muita Mídia',
  description: 'Passo a passo completo, do primeiro contato pelo WhatsApp até a transferência assistida e o suporte pós-venda.',
  step: howToSteps.map(step => ({
    '@type': 'HowToStep',
    name: step.title,
    text: step.description,
  })),
}

const crumbs = breadcrumbSchema([
  { name: 'Início', path: '/' },
  { name: 'Como Funciona', path: '/como-funciona' },
])

const paymentOptions = [
  {
    title: 'Pix (recomendado)',
    description: 'Pagamento à vista com desconto especial e confirmação instantânea. É a forma mais rápida de receber sua página.',
  },
  {
    title: 'Cartão de crédito em até 12x',
    description: 'Parcele a compra em até 12 vezes. Os juros do link de pagamento são de responsabilidade do cliente.',
  },
]

function ComoFunciona() {
  return (
    <Layout>
      <Seo
        title="Como Funciona a Compra de uma Página do Instagram | Muita Mídia"
        description="Entenda o passo a passo completo para comprar uma página do Instagram com a Muita Mídia: contato, escolha do nicho, pagamento, entrega e transferência assistida."
        path="/como-funciona"
        jsonLd={[howToSchema, crumbs]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B0919]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-4xl px-6 py-16 text-center sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#C4B5FD]">
            Como funciona
          </span>
          <h1 className="mt-6 text-3xl font-bold leading-tight text-white sm:text-4xl">
            Do primeiro contato ao controle total do perfil, em poucos minutos
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-400 sm:text-lg">
            Veja em detalhes cada etapa da compra de uma página do Instagram com a Muita Mídia, incluindo
            formas de pagamento e o processo de transferência assistida.
          </p>
        </div>
      </section>

      {/* PASSO A PASSO DETALHADO */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">O passo a passo completo</h2>
            <p className="mt-3 text-base text-zinc-600">
              Cada etapa é acompanhada pela nossa equipe até você ter total controle da sua página.
            </p>
          </div>

          <div className="mt-12 space-y-6">
            {howToSteps.map((step, index) => (
              <div key={step.title} className="flex gap-5 rounded-2xl border border-zinc-100 bg-zinc-50 p-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#8B5CF6] text-sm font-bold text-white">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        title="Já sabe o que procura?"
        description="Fale agora com nossa equipe pelo WhatsApp e comece o processo de compra da sua página."
        message={whatsappMessages.howItWorksCta}
        tone="dark"
      />

      {/* PAGAMENTO */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-zinc-900 sm:text-3xl">Formas de pagamento</h2>
            <p className="mt-3 text-base text-zinc-600">
              Você escolhe a forma de pagamento mais conveniente. A confirmação libera a entrega imediata.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {paymentOptions.map(option => (
              <div key={option.title} className="rounded-2xl border border-zinc-100 bg-zinc-50 p-6">
                <h3 className="text-lg font-semibold text-zinc-900">{option.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERIFICAÇÃO */}
      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">O que garantimos em cada transferência</h2>
            <p className="mt-3 text-base text-zinc-400">
              Estes pontos são verificados e acompanhados em toda compra realizada com a Muita Mídia.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {verificationChecklist.map(item => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#4ADE80]" />
                <span className="text-sm leading-relaxed text-zinc-300">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href={whatsappLink(whatsappMessages.howItWorksCta)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/20 transition-transform hover:scale-105"
            >
              <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
              Iniciar minha compra
            </a>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default ComoFunciona
